import { useState, useCallback, useMemo } from 'react';
import { AxiosInstance } from 'axios';
import { get } from 'lodash/object';

import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';
import { Venue as VenueWithSpaces, fixVenue } from 'hooks/useVenue';
import { RESULTS_PER_PAGE } from 'constants/fieldData';
import { sortFields } from 'constants/sortFields';

type Venue = Omit<VenueWithSpaces, 'spaces'>;
type LatLng = Venue['latLng'];

export interface Filters {
  latLng?: LatLng;
  distance?: number;
  location?: string;
  sort?: keyof typeof sortFields;
  excludedVenueIds?: number[];
  dir?: 'asc' | 'desc';
}

export interface Options<F = Filters> {
  page?: number;
  perPage?: number;
  viewType?: string;
  filters?: F;
}

export interface VenueSearchData<V extends Venue = Venue> {
  venues: V[];
  alternativeVenues: V[];
  totalVenues: number;
  unfilteredTotalVenues: number;
}

export const getSearchData = (
  venues: Venue[] = [],
  totalVenues: number = venues.length
): VenueSearchData => ({
  venues,
  alternativeVenues: [],
  totalVenues,
  unfilteredTotalVenues: totalVenues,
});

export const emptyData = getSearchData();

export const fixData = <Data extends VenueSearchData = VenueSearchData>(
  data: Data
): Data => ({
  ...data,
  venues: data.venues.map(fixVenue),
  alternativeVenues: (data.alternativeVenues ?? []).map(fixVenue),
});

const fixLatLng = ({ lat, lng }: LatLng): LatLng => ({ lat, lng });

export const getParams = <F = Filters>({
  filters: { sort = 'Name', distance = 0, latLng, location, ...rest } = {},
  viewType = '',
  page = 1,
  perPage = RESULTS_PER_PAGE,
}: Options<F> = {}) => ({
  ...rest,
  ...(latLng ? fixLatLng(latLng) : { ...(location ? { location } : {}) }),
  ...(distance > 0 ? { distance } : {}),
  ...(viewType === 'map'
    ? { perPage: 9999, mapView: viewType === 'map', includeSpaces: false }
    : {
        sort: get(sortFields, `${sort}.field`, 'name'),
        dir: get(sortFields, `${sort}.dir`, 'asc'),
        perPage,
        page,
      }),
});

export const getVenueSearch = (api: AxiosInstance, options: Options = {}) =>
  api
    .get<VenueSearchData>('/v1/venues/search', { params: getParams(options) })
    .then(({ data }) => fixData(data));

export const paginateData = <Data extends VenueSearchData = VenueSearchData>(
  nextData: Data,
  page: number = 1
) => (prevData: Data): Data =>
  page > 1
    ? {
        ...prevData,
        venues: [...prevData.venues, ...nextData.venues],
        alternativeVenues: [
          ...prevData.alternativeVenues,
          ...nextData.alternativeVenues,
        ],
      }
    : nextData;

const useVenueSearch = (options: Options = {}) => {
  const [data, setData] = useState<VenueSearchData | undefined>();

  const apiRequest = useApiRequest();

  const { page = 1 } = options;

  const optionsJson = useMemo(() => JSON.stringify(options), [options]);

  const fetchData = useCallback(async () => {
    const nextData = await getVenueSearch(apiRequest, JSON.parse(optionsJson));

    setData(paginateData(nextData, page));
  }, [page, apiRequest, optionsJson]);

  const { loading, error, refresh } = useAsync(fetchData);

  return { data, loading, error, refresh };
};

export default useVenueSearch;
